/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { ColumnVisibility } from "@/components/data-table/column-visiblity";
import { DatePickerWithRange } from "@/components/date-range";
import { ChartConfig, ChartTooltip } from "@/components/ui/chart";
import { CalendarDays, Download, Loader2, Mail, Users2, Mail as MailIcon, ArrowLeft, FileText, ChevronDown, ChevronUp, Info, Globe, ArrowRight, Copy, X, Calendar, Hash, Network, CheckCircle, AlertCircle } from "lucide-react";
import { Area, AreaChart, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { EmailCategoryChart } from "@/pages/reputation/charts/email-category";
import { InboxSpamChart } from "@/pages/reputation/charts/inbox-spam";
import { createFileRoute } from "@tanstack/react-router";
import { ColDef, ValueGetterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { format } from "date-fns";

import TopStatCards from "@/components/dashboard/top-stat-cards";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useGetDashboardStatistics } from "@/hooks/useDashboardsStats";
import { useFetchHostnames, useGetSendersWithTemplates, useGetSenderTemplates, useDownloadFailedHeaders } from "@/hooks/use-statistics";
import { useServices } from "@/services/hooks/useServices";
import { DailyStatistic, DashboardSummary, SenderWithTemplates, EmailTemplate } from "@/types/dashboard.types";
import { authProviderIcons } from "@/pages/mailbox/esp-providers";
import { parseRawEmail, ParsedEmail } from "@/utils/email-parser";
import { toast } from "sonner";
import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { DateRange } from "react-day-picker";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

// Helper function to format date as YYYY-MM-DD in local timezone (not UTC)
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const Route = createFileRoute("/_private/dashboard")({
  component: RouteComponent,
});

const chartConfig = {
  emailsSent: {
    label: "Emails Sent",
    color: "hsl(var(--primary))",
  },
} satisfies ChartConfig;

// Email Templates Modal Component
interface EmailTemplatesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  senders: SenderWithTemplates[];
  isLoadingSenders: boolean;
  onSelectSender: (email: string) => void;
  selectedSenderEmail: string;
  templates: EmailTemplate[];
  isLoadingTemplates: boolean;
  startDate: string;
  endDate: string;
}

const EmailTemplatesModal = ({
  open,
  onOpenChange,
  senders,
  isLoadingSenders,
  onSelectSender,
  selectedSenderEmail,
  templates,
  isLoadingTemplates,
  startDate,
  endDate,
}: EmailTemplatesModalProps) => {
  const [expandedSenders, setExpandedSenders] = useState<Set<string>>(new Set());
  const [expandedESPs, setExpandedESPs] = useState<Set<string>>(new Set());
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [parsedEmail, setParsedEmail] = useState<ParsedEmail | null>(null);
  const [isParsingEmail, setIsParsingEmail] = useState(false);
  const [isReceivingChainOpen, setIsReceivingChainOpen] = useState(true);

  // Sort senders by templateCount (highest first)
  const sortedSenders = useMemo(() => {
    return [...senders].sort((a, b) => b.templateCount - a.templateCount);
  }, [senders]);

  // Group templates by seed_esp (Recipient ESP) - fallback to esp for backward compatibility
  const groupedTemplates = useMemo(() => {
    if (!templates || templates.length === 0) return {};
    
    return templates.reduce((acc, template) => {
      const espKey = template.seed_esp || template.esp;
      if (!acc[espKey]) {
        acc[espKey] = [];
      }
      acc[espKey].push(template);
      return acc;
    }, {} as Record<string, EmailTemplate[]>);
  }, [templates]);

  const toggleSender = (email: string) => {
    setExpandedSenders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
        // Close all ESPs for this sender
        Object.keys(groupedTemplates).forEach(esp => {
          setExpandedESPs(prevESPs => {
            const newESPs = new Set(prevESPs);
            newESPs.delete(`${email}_${esp}`);
            return newESPs;
          });
        });
        onSelectSender("");
      } else {
        newSet.add(email);
        // Fetch templates for this sender
        onSelectSender(email);
      }
      return newSet;
    });
  };

  const toggleESP = (senderEmail: string, esp: string) => {
    const key = `${senderEmail}_${esp}`;
    setExpandedESPs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setExpandedSenders(new Set());
      setExpandedESPs(new Set());
      setSelectedTemplate(null);
      setParsedEmail(null);
      setIsParsingEmail(false);
      setIsReceivingChainOpen(true);
      onSelectSender("");
    }, 200);
  };

  const handleTemplateClick = async (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsParsingEmail(true);
    setParsedEmail(null);
    
    try {
      if (template.raw_mail) {
        const parsed = await parseRawEmail(template.raw_mail);
        setParsedEmail(parsed);
      } else {
        // Fallback to legacy fields
        setParsedEmail({
          subject: template.subject || "",
          html: template.body || "",
          text: template.body ? template.body.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim() : "",
          from: undefined,
          to: undefined,
          date: template.date ? new Date(template.date) : undefined,
          messageId: template.templateId || "",
          headers: {},
        });
      }
    } catch (error) {
      console.error("Error parsing email:", error);
      // Fallback to legacy fields on error
      setParsedEmail({
        subject: template.subject || "",
        html: template.body || "",
        text: template.body ? template.body.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim() : "",
        from: undefined,
        to: undefined,
        date: template.date ? new Date(template.date) : undefined,
        messageId: template.templateId || "",
        headers: {},
      });
    } finally {
      setIsParsingEmail(false);
    }
  };

  const handleBackFromPreview = () => {
    setSelectedTemplate(null);
    setParsedEmail(null);
    setIsParsingEmail(false);
    setIsReceivingChainOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {selectedTemplate && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackFromPreview}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <DialogTitle>
              {selectedTemplate ? "Template Preview" : "Email Templates"}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="mt-4 flex-1 overflow-hidden">
          {/* Template Preview View */}
          {selectedTemplate ? (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
              {isParsingEmail ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Parsing email...</span>
                </div>
              ) : parsedEmail ? (
                <>
                  {/* Email Metadata */}
                  <div className="p-4 bg-muted rounded-lg border">
                    <div className="text-sm font-semibold mb-4">Email Information</div>
                    <div className="space-y-4">
                      {/* To */}
                      <div className="flex items-start gap-3">
                        <Mail className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground mb-1">To:</div>
                          <div className="font-medium break-words">{parsedEmail.to?.address || "-"}</div>
                        </div>
                      </div>

                      {/* From */}
                      <div className="flex items-start gap-3">
                        <Users2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground mb-1">From:</div>
                          <div className="font-medium break-words">{parsedEmail.from?.address || selectedSenderEmail}</div>
                        </div>
                      </div>

                      {/* IP */}
                      <div className="flex items-start gap-3">
                        <Network className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground mb-1">IP:</div>
                          <div className="font-medium font-mono text-sm break-all">{parsedEmail.senderIP || "-"}</div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="flex items-start gap-3">
                        <MailIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground mb-1">Subject:</div>
                          <div className="font-medium break-words whitespace-normal">{parsedEmail.subject || selectedTemplate.subject || "No Subject"}</div>
                        </div>
                      </div>

                      {/* Date */}
                      <div className="flex items-start gap-3">
                        <Calendar className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground mb-1">Date:</div>
                          <div className="font-medium">
                            {parsedEmail.date ? parsedEmail.date.toLocaleString("en-US", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            }) : (selectedTemplate.date ? new Date(selectedTemplate.date).toLocaleString("en-US", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            }) : "-")}
                          </div>
                        </div>
                      </div>

                      {/* Message ID */}
                      <div className="flex items-start gap-3">
                        <Hash className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground mb-1">Message ID:</div>
                          <div className="font-medium font-mono text-sm break-all word-break break-words">{parsedEmail.messageId || selectedTemplate.templateId || "-"}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Raw Email Headers Section */}
                  {selectedTemplate.raw_mail && (
                    <div className="p-4 bg-muted rounded-lg border">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold">Raw Email Headers</div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const headersText = selectedTemplate.raw_mail?.split("\r\n\r\n")[0] || selectedTemplate.raw_mail?.split("\n\n")[0] || selectedTemplate.raw_mail;
                              navigator.clipboard.writeText(headersText ?? "");
                              toast.success("Headers copied to clipboard");
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const blob = new Blob([selectedTemplate.raw_mail || ""], { type: "text/plain" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `headers-${selectedTemplate.templateId || "template"}.txt`;
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </div>
                      <div className="p-3 bg-background rounded border font-mono text-xs text-muted-foreground max-h-[300px] overflow-y-auto overflow-x-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
                        <pre className="whitespace-pre-wrap break-words">
                          {selectedTemplate.raw_mail.split("\r\n\r\n")[0] || selectedTemplate.raw_mail.split("\n\n")[0] || selectedTemplate.raw_mail}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Receiving Chain Section */}
                  {parsedEmail.receivingChain && parsedEmail.receivingChain.length > 0 && (
                    <Collapsible open={isReceivingChainOpen} onOpenChange={setIsReceivingChainOpen}>
                      <div className="p-4 bg-muted rounded-lg border">
                        <CollapsibleTrigger className="w-full flex items-center justify-between hover:bg-muted/50 transition-colors rounded p-2 -m-2">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            <div className="text-sm font-semibold">Email Delivery Chain</div>
                            <TooltipProvider>
                              <UITooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>More Info</p>
                                </TooltipContent>
                              </UITooltip>
                            </TooltipProvider>
                          </div>
                          {isReceivingChainOpen ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mt-3">
                            <div className="text-xs text-muted-foreground mb-3 flex items-center gap-2">
                              <Globe className="h-3 w-3" />
                              <span>Receiving Chain</span>
                              <TooltipProvider>
                                <UITooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Step-by-step routing and processing time of your email across SMTP servers.</p>
                                  </TooltipContent>
                                </UITooltip>
                              </TooltipProvider>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Delay</TableHead>
                                    <TableHead>From</TableHead>
                                    <TableHead className="w-4"></TableHead>
                                    <TableHead>To</TableHead>
                                    <TableHead>Protocol</TableHead>
                                    <TableHead>Time received</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {parsedEmail.receivingChain.map((item) => (
                                    <TableRow key={item.index}>
                                      <TableCell className="font-medium">{item.index}</TableCell>
                                      <TableCell>
                                        <span className="text-green-600 dark:text-green-400">{item.delay}</span>
                                      </TableCell>
                                      <TableCell className="font-mono text-xs break-all">{item.from}</TableCell>
                                      <TableCell>
                                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                      </TableCell>
                                      <TableCell className="font-mono text-xs break-all">{item.to}</TableCell>
                                      <TableCell>{item.protocol}</TableCell>
                                      <TableCell className="text-xs">{item.timeReceived}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}

                  {/* Email Content with Tabs */}
                  <div className="space-y-2">
                    <div className="text-sm font-semibold">Email Content</div>
                    <Tabs defaultValue="rendered" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="plain-text">Plain Text</TabsTrigger>
                        <TabsTrigger value="rendered">Rendered</TabsTrigger>
                      </TabsList>
                      <TabsContent value="plain-text" className="mt-4">
                        <div className="p-4 bg-background rounded-lg border min-h-[400px]">
                          <div className="text-sm whitespace-pre-wrap break-words">
                            {parsedEmail.text || (parsedEmail.html ? parsedEmail.html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim() : "")}
                          </div>
                        </div>
                      </TabsContent>
                      <TabsContent value="rendered" className="mt-4">
                        <div className="border rounded-lg overflow-hidden bg-white">
                          <iframe
                            srcDoc={parsedEmail.html || parsedEmail.text || selectedTemplate.body || ""}
                            sandbox="allow-same-origin"
                            className="w-full h-[600px] border-0"
                            title={`Email Template - ${parsedEmail.subject || selectedTemplate.subject || "No Subject"}`}
                          />
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            /* Senders List View */
            <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
              {isLoadingSenders ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Loading senders...</span>
                </div>
              ) : sortedSenders.length > 0 ? (
                sortedSenders.map((sender) => {
                  const isSenderExpanded = expandedSenders.has(sender.sender_email);
                  const isLoading = isSenderExpanded && selectedSenderEmail === sender.sender_email && isLoadingTemplates;
                  const senderTemplates = selectedSenderEmail === sender.sender_email ? groupedTemplates : {};

                  return (
                    <div
                      key={sender.sender_email}
                      className="border rounded-lg overflow-hidden bg-background"
                    >
                      {/* Sender Header - Collapsible */}
                      <div
                        onClick={() => toggleSender(sender.sender_email)}
                        className="p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="bg-primary/10 p-2 rounded-full">
                            <Users2 className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-sm">{sender.sender_email}</div>
                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                              <span>
                                {sender.templateCount} template{sender.templateCount !== 1 ? "s" : ""} • {sender.espCount} ESP{sender.espCount !== 1 ? "s" : ""}
                              </span>
                              {sender.esps && sender.esps.length > 0 && (
                                <>
                                  <span>•</span>
                                  <div className="flex items-center gap-1.5">
                                    {sender.esps.map((esp) => {
                                      const espTypeStr = String(esp);
                                      const iconMapping = authProviderIcons[espTypeStr] || authProviderIcons[espTypeStr.toLowerCase()] || authProviderIcons["smtp"];
                                      return (
                                        <TooltipProvider key={esp}>
                                          <UITooltip>
                                            <TooltipTrigger asChild>
                                              <div className="cursor-help">
                                                {iconMapping?.icon || <Mail className="h-4 w-4" />}
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>{esp.replace(/_/g, " ")}</p>
                                            </TooltipContent>
                                          </UITooltip>
                                        </TooltipProvider>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          {/* <div className="flex items-center gap-2">
                            {sender.esps.map((esp) => (
                              <Badge key={esp} variant="secondary" className="text-xs">
                                {esp}
                              </Badge>
                            ))}
                          </div> */}
                          {isSenderExpanded ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground ml-2" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground ml-2" />
                          )}
                        </div>
                      </div>

                      {/* Expanded Content - Templates Grouped by ESP */}
                      {isSenderExpanded && (
                        <div className="border-t bg-muted/30">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="h-5 w-5 animate-spin mr-2" />
                              <span className="text-sm text-muted-foreground">Loading templates...</span>
                            </div>
                          ) : Object.keys(senderTemplates).length > 0 ? (
                            Object.entries(senderTemplates).map(([esp, espTemplates]) => {
                              const espKey = `${sender.sender_email}_${esp}`;
                              const isESPExpanded = expandedESPs.has(espKey);

                              return (
                                <div key={esp} className="border-b last:border-b-0">
                                  {/* ESP Header - Collapsible */}
                                  <div
                                    onClick={() => {
                                      // If there's only one template for this ESP, open it directly
                                      if (espTemplates.length === 1) {
                                        handleTemplateClick(espTemplates[0]);
                                      } else {
                                        toggleESP(sender.sender_email, esp);
                                      }
                                    }}
                                    className="p-4 hover:bg-muted/30 transition-colors cursor-pointer flex items-center justify-between"
                                  >
                                    <div className="flex items-center gap-2">
                                      <TooltipProvider>
                                        <UITooltip>
                                          <TooltipTrigger asChild>
                                            <div className="flex items-center gap-2">
                                              {(() => {
                                                const espTypeStr = String(esp);
                                                const iconMapping = authProviderIcons[espTypeStr] || authProviderIcons[espTypeStr.toLowerCase()] || authProviderIcons["smtp"];
                                                return iconMapping?.icon || <Mail className="h-4 w-4" />;
                                              })()}
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>{esp.replace(/_/g, " ")}</p>
                                          </TooltipContent>
                                        </UITooltip>
                                      </TooltipProvider>
                                      <Badge variant="outline" className="uppercase text-xs">
                                        {esp.replace(/_/g, " ")}
                                      </Badge>
                                    </div>
                                    {isESPExpanded ? (
                                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </div>

                                  {/* ESP Templates - Expanded */}
                                  {isESPExpanded && (
                                    <div className="bg-background">
                                      {espTemplates
                                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                                        .map((template, idx) => (
                                          <div
                                            key={template.templateId}
                                            className="p-4 space-y-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 transition-colors"
                                            onClick={() => handleTemplateClick(template)}
                                          >


                                            {/* Subject */}
                                            <div className="flex items-center gap-4">
                                              <div className="text-xs text-muted-foreground uppercase">
                                                SUBJECT :
                                              </div>
                                              <div className="font-semibold text-sm">
                                                {template.raw_mail 
                                                  ? (() => {
                                                      const subjectMatch = template.raw_mail.match(/Subject:\s*(.+?)(?:\r?\n|$)/i);
                                                      return subjectMatch ? subjectMatch[1].trim() : template.subject || "No Subject";
                                                    })()
                                                  : (template.subject || "No Subject")
                                                }
                                              </div>
                                            </div>

                                            {/* Email Content Label */}
                                            <TooltipProvider>
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground uppercase">
                                                  Template Body
                                                </span>
                                                <UITooltip>
                                                  <TooltipTrigger asChild>
                                                    <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                                                  </TooltipTrigger>
                                                  <TooltipContent>
                                                    <p>Click on the content to view the template</p>
                                                  </TooltipContent>
                                                </UITooltip>
                                              </div>
                                            </TooltipProvider>

                                            {/* Template Preview Snippet */}
                                            <div className="p-3 bg-muted rounded border text-xs text-muted-foreground line-clamp-3">
                                              {template.raw_mail
                                                ? (() => {
                                                    // Extract body from raw_mail (content after headers)
                                                    const headerEnd = template.raw_mail.indexOf("\r\n\r\n") !== -1 
                                                      ? template.raw_mail.indexOf("\r\n\r\n") 
                                                      : template.raw_mail.indexOf("\n\n");
                                                    const body = headerEnd !== -1 
                                                      ? template.raw_mail.substring(headerEnd + 2) 
                                                      : template.raw_mail;
                                                    const textBody = body.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
                                                    return textBody.substring(0, 200) + (textBody.length > 200 ? "..." : "");
                                                  })()
                                                : (template.body ? template.body.replace(/<[^>]*>/g, "").substring(0, 200) + "..." : "No content")
                                              }
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <p className="text-muted-foreground text-center py-8 text-sm">
                              No templates found
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No senders with templates found
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

type DashboardCard = {
  id: string;
  component: React.ReactNode;
};

function RouteComponent() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    to: new Date(),
    from: new Date(new Date().setDate(new Date().getDate() - 7)),
  });
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState<string>("");
  const [templateModalDateRange, setTemplateModalDateRange] = useState<{ startDate: string; endDate: string } | null>(null);
  
  const { data: dashboardStats, isLoading: dashboardStatsLoading } =
    useGetDashboardStatistics({
      startDate: dateRange?.from ?? new Date(),
      endDate: dateRange?.to ?? new Date(),
    });

  // Calculate total templates
  const totalTemplates = useMemo(() => {
    return dashboardStats?.templateSummary?.reduce(
      (sum, s) => sum + s.templateCount,
      0
    ) || 0;
  }, [dashboardStats?.templateSummary]);

  // Format dates for API calls
  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const startDateStr = dateRange?.from ? formatDateForAPI(dateRange.from) : formatDateForAPI(new Date());
  const endDateStr = dateRange?.to ? formatDateForAPI(dateRange.to) : formatDateForAPI(new Date());

  // Get senders with templates - when modal is open (either from dashboard button or grid)
  const { data: sendersData, isLoading: isLoadingSenders } = useGetSendersWithTemplates({
    startDate: templateModalDateRange?.startDate || startDateStr,
    endDate: templateModalDateRange?.endDate || endDateStr,
    enabled: showTemplateModal,
  });

  // Get templates for selected sender
  const { data: templatesData, isLoading: isLoadingTemplates } = useGetSenderTemplates({
    senderEmail: selectedSenderEmail,
    startDate: templateModalDateRange?.startDate || startDateStr,
    endDate: templateModalDateRange?.endDate || endDateStr,
    enabled: showTemplateModal && !!selectedSenderEmail,
  });

  const chartConfig = {
    rate: {
      label: "Spam Rate",
      color: "hsl(221, 83%, 53%)",
    },
    emailsSent: {
      label: "Emails Sent",
      color: "hsl(var(--primary))",
    },
  } satisfies ChartConfig;

  // Helper function to format date range text
  const formatDateRangeText = (dateRange: DateRange | undefined): string => {
    if (!dateRange?.from) return "Select date range";
    
    if (dateRange.to) {
      // Calculate days difference (inclusive of both start and end dates)
      const daysDiff = Math.ceil(
        (dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;
      
      // If it's a multiple of 7 days, show weeks
      if (daysDiff % 7 === 0) {
        const weeks = daysDiff / 7;
        return `Last ${weeks} ${weeks === 1 ? 'week' : 'weeks'} performance`;
      }
      
      // Otherwise show days
      return `Last ${daysDiff} ${daysDiff === 1 ? 'day' : 'days'} performance`;
    }
    
    // Single date selected
    return "Last 1 day performance";
  };

  const dashboardCards = useMemo<DashboardCard[]>(
    () => [
      {
        id: "email-sent",
        component: !dashboardStatsLoading ? (
          <EmailSentChart
            data={
              dashboardStats?.mailboxSeedDailyStats as Array<{
                sentDate: string;
                count: number;
              }>
            }
            dailyStats={dashboardStats?.dailyStats || []}
            dailyEmailsSent={dashboardStats?.dailyEmailsSent || []}
            className="w-full"
          />
        ) : (
          <Card className="w-full h-[400px]">
            <CardHeader>
              <Skeleton className="h-7 w-40" />
              <Skeleton className="h-5 w-64 mt-1" />
            </CardHeader>
            <CardContent>
              <Skeleton className="w-full h-[270px]" />
            </CardContent>
          </Card>
        ),
      },
      {
        id: "inbox-spam",
        component: !dashboardStatsLoading ? (
          <InboxSpamChart
            data={dashboardStats?.deliveryPerformance || []}
            className="w-full"
            isLoading={false}
          />
        ) : (
          <Card className="w-full h-[400px]">
            <CardHeader>
              <Skeleton className="h-7 w-40" />
              <Skeleton className="h-5 w-64 mt-1" />
            </CardHeader>
            <CardContent>
              <Skeleton className="w-full h-[270px]" />
            </CardContent>
          </Card>
        ),
      },
      {
        id: "email-category",
        component: !dashboardStatsLoading ? (
          <EmailCategoryChart
            data={dashboardStats?.categoryStats || []}
            colors={{
              Primary: "hsl(142, 76%, 36%)", // Green
              Promotions: "hsl(221, 83%, 53%)", // Blue
              Updates: "hsl(48, 96%, 53%)", // Yellow
              Spam: "hsl(0, 84%, 60%)", // Red
            }}
            className="w-full"
            isLoading={false}
          />
        ) : (
          <Card className="w-full h-[400px]">
            <CardHeader>
              <Skeleton className="h-7 w-40" />
              <Skeleton className="h-5 w-64 mt-1" />
            </CardHeader>
            <CardContent>
              <Skeleton className="w-full h-[270px]" />
            </CardContent>
          </Card>
        ),
      },
      {
        id: "spam-rate",
        component: !dashboardStatsLoading ? (
          <Card className="h-full">
            <CardHeader className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Email Spam Rate</CardTitle>
                  <CardDescription className="mt-1">
                    Daily percentage of emails marked as spam
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col h-[calc(100%-100px)] p-4">
              <div className="flex-1">
                <ChartContainer className="w-full h-full" config={chartConfig}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dashboardStats?.spamRate || []}>
                      <defs>
                        <linearGradient
                          id="colorRate"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="hsl(0 84.2% 60.2%)"
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="95%"
                            stopColor="hsl(0 84.2% 60.2%)"
                            stopOpacity={0.05}
                          />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tick={{
                          fill: "hsl(var(--muted-foreground))",
                          fontSize: 12,
                        }}
                        dy={10}
                        tickFormatter={(value) => {
                          const dateObj = new Date(value);
                          if (isNaN(dateObj.getTime())) {
                            return "Invalid Date";
                          }
                          return dateObj.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          });
                        }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tick={{
                          fill: "hsl(var(--muted-foreground))",
                          fontSize: 12,
                        }}
                        tickFormatter={(value) => `${value.toFixed(1)}%`}
                        dx={-10}
                        domain={[0, "auto"]}
                        allowDecimals={true}
                        tickCount={6}
                      />
                      <ChartTooltip
                        content={({ active, payload }) => {
                          if (active && payload?.length) {
                            return (
                              <div className="rounded-lg border bg-background p-3 shadow-lg">
                                <div className="grid gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground font-medium">
                                      Date
                                    </span>
                                    <span className="font-semibold text-foreground">
                                      {payload[0].payload.date}
                                    </span>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground font-medium">
                                      Spam Rate
                                    </span>
                                    <span className="font-semibold text-primary text-lg">
                                      {/* {payload[0].value} */}
                                      {payload[0].value
                                        ? parseInt(
                                            payload[0].value as string
                                          ).toFixed(2)
                                        : 0}
                                      %
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="rate"
                        stroke="hsl(0 84.2% 60.2%)"
                        fill="url(#colorRate)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
              <div className="mt-6 flex items-center text-sm text-muted-foreground border-t pt-4">
                <CalendarDays className="w-4 h-4 mr-2 text-muted-foreground/70" />
                <span className="font-medium">{formatDateRangeText(dateRange)}</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="w-full h-[400px]">
            <CardHeader>
              <Skeleton className="h-7 w-40" />
              <Skeleton className="h-5 w-64 mt-1" />
            </CardHeader>
            <CardContent>
              <Skeleton className="w-full h-[270px]" />
            </CardContent>
          </Card>
        ),
      },
      {
        id: "domain-status",
        component: !dashboardStatsLoading ? (
          <DomainStatusChart
            dashboardStats={
              dashboardStats?.postmasterInsights || {
                totalValue: 0,
                highReputation: { value: 0, percentage: 0 },
                goodSpamRate: { value: 0, percentage: 0 },
                needsAttention: { value: 0, percentage: 0 },
              }
            }
          />
        ) : (
          <Card className="w-full h-[400px]">
            <CardHeader>
              <Skeleton className="h-7 w-40" />
              <Skeleton className="h-5 w-64 mt-1" />
            </CardHeader>
            <CardContent>
              <Skeleton className="w-full h-[270px]" />
            </CardContent>
          </Card>
        ),
      },
    ],
    [dashboardStats, dashboardStatsLoading, chartConfig, dateRange]
  );

  const [cardOrder, setCardOrder] = useState<string[]>(() =>
    dashboardCards.map((card) => card.id)
  );

  const moveCard = (fromIndex: number, toIndex: number) => {
    const newOrder = [...cardOrder];
    const [movedId] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedId);
    setCardOrder(newOrder);
  };

  // Render cards in the correct order
  const orderedCards = cardOrder.map((id) =>
    dashboardCards.find((card) => card.id === id)
  );

  return (
    <div className="grid grid-cols-12 gap-4 w-full">
      <div className="col-span-full flex flex-col sm:flex-row justify-between sm:items-center gap-2">
        <h1 className="page-heading">Dashboard</h1>
        <div className="flex gap-2">
          <DatePickerWithRange
            align="end"
            date={dateRange}
            onDateChange={(date) => setDateRange(date)}
          />
        </div>
      </div>

      {/* Top Stat Cards */}
      {dashboardStatsLoading ? (
        // Loading skeleton for stat cards
        <div className="col-span-full grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="w-full">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Skeleton className="size-8 shrink-0" />
                  <Skeleton className="h-5 w-40 sm:w-full" />
                </div>
                <Skeleton className="h-8 max-w-40 sm:w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <TopStatCards
          senderStats={{
            summary: dashboardStats?.summary as DashboardSummary,
          }}
        />
      )}

      {/* Charts Section */}
      <DndProvider backend={HTML5Backend}>
        <div className="col-span-full grid grid-cols-12 gap-4">
          {/* Email Sent Chart - Full Width */}
          <DraggableCard
            key="email-sent"
            index={0}
            moveCard={moveCard}
            className="col-span-12"
          >
            {orderedCards[0]?.component}
          </DraggableCard>

          {/* Two Column Layout for Other Charts */}
          <div className="col-span-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            {orderedCards.slice(1).map((card, index) => (
              <DraggableCard
                key={card?.id}
                index={index + 1}
                moveCard={moveCard}
                className="col-span-1"
              >
                {card?.component}
              </DraggableCard>
            ))}
          </div>
        </div>
      </DndProvider>

      {/* Table Section */}
        <EmailStatisticTable
          data={dashboardStats?.dailyStats || []}
          dailyEmailsSent={dashboardStats?.dailyEmailsSent || []}
          isLoading={dashboardStatsLoading}
          dateRange={dateRange}
          onOpenTemplateModal={(date) => {
            setTemplateModalDateRange({
              startDate: date,
              endDate: date,
            });
            setShowTemplateModal(true);
            setSelectedSenderEmail("");
          }}
        />

      {/* Email Templates Modal */}
      <EmailTemplatesModal
        open={showTemplateModal}
        onOpenChange={(open) => {
          setShowTemplateModal(open);
          if (!open) {
            setTemplateModalDateRange(null);
            setSelectedSenderEmail("");
          }
        }}
        senders={sendersData?.senders || []}
        isLoadingSenders={isLoadingSenders}
        onSelectSender={setSelectedSenderEmail}
        selectedSenderEmail={selectedSenderEmail}
        templates={templatesData?.templates || []}
        isLoadingTemplates={isLoadingTemplates}
        startDate={templateModalDateRange?.startDate || startDateStr}
        endDate={templateModalDateRange?.endDate || endDateStr}
      />
    </div>
  );
}

type EmailStatistic = {
  date: string;
  opens: number;
  stars: number;
  links_clicked: number;
  replies: number;
  moved_primary: number;
  inbox: number;
  spam: number;
  mailboxtemperature: number;
  send: number;
  clicks: number;
};

const columns: ColDef<DailyStatistic>[] = [
  {
    headerName: "Date",
    field: "date",
    minWidth: 150,
    valueFormatter: (params) => {
      const dateObj = new Date(params.value);
      if (isNaN(dateObj.getTime())) {
        return "Invalid Date";
      }
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  {
    headerName: "Warmup Email Sent",
    field: "inbox",
    type: "numericColumn",
    minWidth: 180,
    valueGetter: (params: any) => {
      if (params.data.send === 0) {
        return 0;
      }
      return Math.max(params.data.send, params.data.opens);
    },
  },
  {
    headerName: "Warmup Email Received",
    field: "opens",
    type: "numericColumn",
  },
  {
    headerName: "Warmup Email Opens",
    field: "opens",
    type: "numericColumn",
  },
  {
    headerName: "Warmup Email Clicks",
    field: "clicks",
    type: "numericColumn",
  },
  {
    headerName: "Warmup Email Replies",
    field: "replies",
    type: "numericColumn",
  },
  {
    headerName: "Warmup Email Stars",
    field: "stars",
    type: "numericColumn",
  },
  {
    headerName: "Emails Received in Inbox",
    field: "inbox",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Email Received in Spam",
    field: "spam",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Email Moved Out of Spam",
    field: "unspam_count",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Engagement Rate",
    field: "opens",
    valueGetter: (params: any) => {
      const totalInteractions = params.data.opens;
      let totalEmails = 0;
      if (params.data.send) {
        totalEmails = params.data.send;
      }
      if (params.data.mailboxtemperature) {
        totalEmails = Math.min(totalEmails, params.data.mailboxtemperature);
      }

      if(totalEmails === 0) totalEmails = params.data.opens;
      const rawPercent = (totalInteractions / totalEmails) * 100;
      const capped = Math.min(rawPercent, 100);
      return `${capped.toFixed(1)}%`;
    },
  },
];

interface EmailStatisticTableProps {
  data: EmailStatistic[];
  isLoading: boolean;
  dateRange?: DateRange;
  dailyEmailsSent?: Array<{ date: string; count: number }>;
}

const EmailStatisticTable = ({ 
  data, 
  isLoading, 
  dateRange,
  dailyEmailsSent,
  onOpenTemplateModal,
}: EmailStatisticTableProps & { onOpenTemplateModal: (date: string) => void }) => {
  const gridRef = useRef<AgGridReact>(null);
  const [showSenderIpsDialog, setShowSenderIpsDialog] = useState(false);
  const [selectedMailboxIps, setSelectedMailboxIps] = useState<string[]>([]);
  const [selectedSenderProfileIps, setSelectedSenderProfileIps] = useState<string[]>([]);
  const [selectedMailboxIpHostnames, setSelectedMailboxIpHostnames] = useState<Record<string, { hostname: string; lastLookup: string }>>({});
  const [selectedSenderProfileIpHostnames, setSelectedSenderProfileIpHostnames] = useState<Record<string, { hostname: string; lastLookup: string }>>({});
  const [selectedMailboxIpCounts, setSelectedMailboxIpCounts] = useState<Record<string, number>>({});
  const [selectedSenderProfileIpCounts, setSelectedSenderProfileIpCounts] = useState<Record<string, number>>({});
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [showEmailTemplateDialog, setShowEmailTemplateDialog] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<string>("");
  const [selectedEmailTemplates, setSelectedEmailTemplates] = useState<Array<{
    sender_email: string;
    name: string;
    subject: string;
    body: string;
  }>>([]);
  const [ipHostnames, setIpHostnames] = useState<Record<string, string>>({});
  const [isLoadingHostnames, setIsLoadingHostnames] = useState(false);
  const [visibleMailboxIpCount, setVisibleMailboxIpCount] = useState(50);
  const [visibleSenderProfileIpCount, setVisibleSenderProfileIpCount] = useState(50);
  const [loadingMoreMailboxIps, setLoadingMoreMailboxIps] = useState(false);
  const [loadingMoreSenderProfileIps, setLoadingMoreSenderProfileIps] = useState(false);
  const [loadedMailboxIpBatches, setLoadedMailboxIpBatches] = useState<Set<number>>(new Set());
  const [loadedSenderProfileIpBatches, setLoadedSenderProfileIpBatches] = useState<Set<number>>(new Set());
  const mailboxScrollContainerRef = useRef<HTMLDivElement>(null);
  const senderProfileScrollContainerRef = useRef<HTMLDivElement>(null);
  const hostnameCacheRef = useRef<Record<string, Record<string, string>>>({});
  const { mutateAsync: fetchHostnames } = useFetchHostnames();
  const { statisticsService } = useServices();
  const [isExportingIps, setIsExportingIps] = useState(false);

  // Download failed headers hoo
  const { mutate: downloadFailedHeaders, isPending: isDownloading } = useDownloadFailedHeaders();
  const [downloadingDate, setDownloadingDate] = useState<string | null>(null);

  const BATCH_SIZE = 50;

  // When dailyStats.send is 0, use dailyEmailsSent count for that date (for Warmup Email Sent & Engagement Rate)
  const emailsSentByDate = useMemo(() => {
    return (dailyEmailsSent || []).reduce(
      (acc, item) => {
        acc[item.date] = item.count ?? 0;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [dailyEmailsSent]);

  // API may return IP count keys with underscores (e.g. 45_112_195_189) while IPs list uses dots (45.112.195.189)
  const getIpCount = useCallback((counts: Record<string, number>, ip: string): number => {
    if (!counts) return 0;
    const withDots = counts[ip];
    if (withDots !== undefined && withDots !== null) return withDots;
    const withUnderscores = counts[ip.replace(/\./g, "_")];
    return withUnderscores !== undefined && withUnderscores !== null ? withUnderscores : 0;
  }, []);

  // Helper function to format date as YYYY-MM-DD
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Mailbox IPs Cell Renderer Component
  const MailboxIpsCellRenderer = (params: ValueGetterParams<DailyStatistic>) => {
    const mailboxIps = (params?.data as any)?.mailboxIps || [];
    // Fallback to old senderIps for backward compatibility
    const legacyIps = (params?.data as any)?.senderIps || [];
    const allMailboxIps = mailboxIps.length > 0 ? mailboxIps : (legacyIps.length > 0 ? legacyIps : []);
    
    const hasMailboxIps = allMailboxIps.length > 0;
    
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (hasMailboxIps) {
        setSelectedMailboxIps(allMailboxIps);
        setSelectedSenderProfileIps([]);
        setSelectedMailboxIpHostnames((params?.data as any)?.mailboxIpHostnames || {});
        setSelectedSenderProfileIpHostnames({});
        setSelectedMailboxIpCounts((params?.data as any)?.mailboxIpCounts || {});
        setSelectedSenderProfileIpCounts({});
        setSelectedDate(params.data?.date || "");
        setShowSenderIpsDialog(true);
      }
    };

    return (
      <button
        className={`px-2 py-1 rounded text-sm transition-colors ${
          hasMailboxIps
            ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
            : "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
        }`}
        disabled={!hasMailboxIps}
        onClick={handleClick}
      >
        {hasMailboxIps ? `${allMailboxIps.length} IP${allMailboxIps.length > 1 ? "s" : ""}` : "No IPs"}
      </button>
    );
  };

  const SpfCellRenderer = (params: ValueGetterParams<DailyStatistic>) => {
    const totalspfPass = (params?.data as any)?.spf_pass_count || 0;
    const totalspfFail = (params?.data as any)?.spf_fail_count || 0;
    const totalspf = totalspfPass + totalspfFail;
    const percentage = totalspf > 0 ? (totalspfPass / totalspf) * 100 : 0;

    let Icon = CheckCircle;
    let colorClass = "text-green-600";

    if (percentage >= 95) {
      Icon = CheckCircle;
      colorClass = "text-green-600";
    } else if (percentage >= 90) {
      Icon = CheckCircle;
      colorClass = "text-yellow-500";
    } else {
      Icon = AlertCircle;
      colorClass = "text-red-500";
    }

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      const date = (params?.data as any)?.date;
      if (date) {
        setDownloadingDate(date);
        downloadFailedHeaders(
          { date, spfResult: true },
          {
            onSuccess: (data) => {
              const url = window.URL.createObjectURL(new Blob([data as any]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", `failed-headers-${date}.zip`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              setDownloadingDate(null);
              toast.success("Download started");
            },
            onError: () => {
              setDownloadingDate(null);
              toast.error("Failed to download headers");
            },
          }
        );
      }
    };

    const isThisDownloading = isDownloading && downloadingDate === (params?.data as any)?.date;

    return (
      <div className="flex items-center mt-2 gap-2">
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-help">
                <Icon className={`h-5 w-5 ${colorClass}`} />
                <span className="font-medium text-sm">{percentage.toFixed(2)}%</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-slate-900 text-white border-none">
              <p>{totalspfPass} passed, {totalspfFail} failed</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>

        {totalspfFail > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 ml-2"
            onClick={handleDownload}
            disabled={isThisDownloading}
          >
            {isThisDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            )}
          </Button>
        )}
      </div>
    );
  };

  const DmarcCellRenderer = (params: ValueGetterParams<DailyStatistic>) => {
    const totaldmarcPass = (params?.data as any)?.dmarc_pass_count || 0;
    const totaldmarcFail = (params?.data as any)?.dmarc_fail_count || 0;
    const totaldmarc = totaldmarcPass + totaldmarcFail;
    const percentage = totaldmarc > 0 ? (totaldmarcPass / totaldmarc) * 100 : 0;

    let Icon = CheckCircle;
    let colorClass = "text-green-600";

    if (percentage >= 95) {
      Icon = CheckCircle;
      colorClass = "text-green-600";
    } else if (percentage >= 90) {
      Icon = CheckCircle;
      colorClass = "text-yellow-500";
    } else {
      Icon = AlertCircle;
      colorClass = "text-red-500";
    }

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      const date = (params?.data as any)?.date;
      if (date) {
        setDownloadingDate(date);
        downloadFailedHeaders(
          { date, dmarcResult: true },
          {
            onSuccess: (data) => {
              const url = window.URL.createObjectURL(new Blob([data as any]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", `failed-headers-${date}.zip`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              setDownloadingDate(null);
              toast.success("Download started");
            },
            onError: () => {
              setDownloadingDate(null);
              toast.error("Failed to download headers");
            },
          }
        );
      }
    };

    const isThisDownloading = isDownloading && downloadingDate === (params?.data as any)?.date;

    return (
      <div className="flex items-center mt-2 gap-2">
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-help">
                <Icon className={`h-5 w-5 ${colorClass}`} />
                <span className="font-medium text-sm">{percentage.toFixed(2)}%</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-slate-900 text-white border-none">
              <p>{totaldmarcPass} passed, {totaldmarcFail} failed</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>

        {totaldmarcFail > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 ml-2"
            onClick={handleDownload}
            disabled={isThisDownloading}
          >
            {isThisDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            )}
          </Button>
        )}
      </div>
    );
  };

  const DkimCellRenderer = (params: ValueGetterParams<DailyStatistic>) => {
    const totaldkimPass = (params?.data as any)?.dkim_pass_count || 0;
    const totaldkimFail = (params?.data as any)?.dkim_fail_count || 0;
    const totaldkim = totaldkimPass + totaldkimFail;
    const percentage = totaldkim > 0 ? (totaldkimPass / totaldkim) * 100 : 0;

    let Icon = CheckCircle;
    let colorClass = "text-green-600";

    if (percentage >= 95) {
      Icon = CheckCircle;
      colorClass = "text-green-600";
    } else if (percentage >= 90) {
      Icon = CheckCircle;
      colorClass = "text-yellow-500";
    } else {
      Icon = AlertCircle;
      colorClass = "text-red-500";
    }

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      const date = (params?.data as any)?.date;
      if (date) {
        setDownloadingDate(date);
        downloadFailedHeaders(
          { date, dkimResult: true },
          {
            onSuccess: (data) => {
              const url = window.URL.createObjectURL(new Blob([data as any]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", `failed-headers-${date}.zip`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              setDownloadingDate(null);
              toast.success("Download started");
            },
            onError: () => {
              setDownloadingDate(null);
              toast.error("Failed to download headers");
            },
          }
        );
      }
    };

    const isThisDownloading = isDownloading && downloadingDate === (params?.data as any)?.date;

    return (
      <div className="flex items-center mt-2 gap-2">
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-help">
                <Icon className={`h-5 w-5 ${colorClass}`} />
                <span className="font-medium text-sm">{percentage.toFixed(2)}%</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-slate-900 text-white border-none">
              <p>{totaldkimPass} passed, {totaldkimFail} failed</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>

        {totaldkimFail > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 ml-2"
            onClick={handleDownload}
            disabled={isThisDownloading}
          >
            {isThisDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            )}
          </Button>
        )}
      </div>
    );
  };

  // Sender Profile IPs Cell Renderer Component
  const SenderProfileIpsCellRenderer = (params: ValueGetterParams<DailyStatistic>) => {
    const senderProfileIps = (params?.data as any)?.senderProfileIps || [];
    
    const hasSenderProfileIps = senderProfileIps.length > 0;
    
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (hasSenderProfileIps) {
        setSelectedMailboxIps([]);
        setSelectedSenderProfileIps(senderProfileIps);
        setSelectedMailboxIpHostnames({});
        setSelectedSenderProfileIpHostnames((params?.data as any)?.senderProfileIpHostnames || {});
        setSelectedMailboxIpCounts({});
        setSelectedSenderProfileIpCounts((params?.data as any)?.senderProfileIpCounts || {});
        setSelectedDate(params.data?.date || "");
        setShowSenderIpsDialog(true);
      }
    };

    return (
      <button
        className={`px-2 py-1 rounded text-sm transition-colors ${
          hasSenderProfileIps
            ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
            : "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
        }`}
        disabled={!hasSenderProfileIps}
        onClick={handleClick}
      >
        {hasSenderProfileIps ? `${senderProfileIps.length} IP${senderProfileIps.length > 1 ? "s" : ""}` : "No IPs"}
      </button>
    );
  };

  // Email Template Cell Renderer Component
  const EmailTemplateCellRenderer = (params: ValueGetterParams<DailyStatistic>) => {
    // Check for new senderProfileTemplates format first (array)
    const senderProfileTemplates = (params?.data as any)?.senderProfileTemplates || [];
    // Fallback to old firstEmailBody for backward compatibility
    const legacyTemplate = (params?.data as any)?.firstEmailBody || "";
    
    const hasTemplates = senderProfileTemplates.length > 0 || (legacyTemplate && legacyTemplate.trim().length > 0);
    const templateCount = senderProfileTemplates.length || (legacyTemplate ? 1 : 0);
    
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (hasTemplates) {
        // Get the date from the row data
        const rowDate = (params?.data as any)?.date;
        if (rowDate) {
          // Call the parent handler to open modal with this date
          onOpenTemplateModal(rowDate);
        }
      }
    };

    return (
      <button
        className={`px-2 py-1 rounded text-sm transition-colors ${
          hasTemplates
            ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
            : "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
        }`}
        disabled={!hasTemplates}
        onClick={handleClick}
      >
        {hasTemplates 
          ? templateCount > 1 
            ? `View ${templateCount} Templates` 
            : "View Template"
          : "No Template"}
      </button>
    );
  };

  // Get visible IPs based on pagination - separate for mailbox and sender profile
  const visibleMailboxIps = useMemo(() => {
    return selectedMailboxIps.slice(0, visibleMailboxIpCount);
  }, [selectedMailboxIps, visibleMailboxIpCount]);

  const visibleSenderProfileIps = useMemo(() => {
    return selectedSenderProfileIps.slice(0, visibleSenderProfileIpCount);
  }, [selectedSenderProfileIps, visibleSenderProfileIpCount]);

  // Extract hostnames from API response data (mailboxIpHostnames or senderProfileIpHostnames)
  const extractHostnamesFromResponse = useCallback((hostnameData: Record<string, { hostname: string; lastLookup: string }>): Record<string, string> => {
    const hostnameMap: Record<string, string> = {};
    Object.entries(hostnameData).forEach(([ip, data]) => {
      if (data?.hostname) {
        hostnameMap[ip] = data.hostname;
      }
    });
    return hostnameMap;
  }, []);

  // Fetch hostnames for a specific batch of IPs (only for IPs that don't have hostnames)
  const fetchHostnamesForBatch = useCallback(async (ips: string[], batchIndex: number, isMailbox: boolean) => {
    const loadedBatches = isMailbox ? loadedMailboxIpBatches : loadedSenderProfileIpBatches;
    const hostnameData = isMailbox ? selectedMailboxIpHostnames : selectedSenderProfileIpHostnames;
    
    if (loadedBatches.has(batchIndex)) {
      return; // Already loaded
    }

    // First, check if hostnames are already in the API response
    const existingHostnames = extractHostnamesFromResponse(hostnameData);
    const ipsNeedingHostnames = ips.filter(ip => !existingHostnames[ip]);
    
    // If all IPs have hostnames, use them from the response
    if (ipsNeedingHostnames.length === 0) {
      setIpHostnames((prev) => ({ ...prev, ...existingHostnames }));
      if (isMailbox) {
        setLoadedMailboxIpBatches((prev) => new Set(prev).add(batchIndex));
      } else {
        setLoadedSenderProfileIpBatches((prev) => new Set(prev).add(batchIndex));
      }
      return;
    }

    // Use the selected date from the row (the date of the IPs), not the dashboard date range
    const dateToUse = selectedDate || formatDateLocal(new Date());
    
    // Create cache key based on date and IPs
    const cacheKey = `${dateToUse}-${ipsNeedingHostnames.sort().join(',')}`;
    
    // Check if we have cached hostnames for this batch
    if (hostnameCacheRef.current[cacheKey]) {
      const cachedHostnames = hostnameCacheRef.current[cacheKey];
      setIpHostnames((prev) => ({ ...prev, ...existingHostnames, ...cachedHostnames }));
      if (isMailbox) {
        setLoadedMailboxIpBatches((prev) => new Set(prev).add(batchIndex));
      } else {
        setLoadedSenderProfileIpBatches((prev) => new Set(prev).add(batchIndex));
      }
      return;
    }

    try {
      const startDate = dateToUse;
      const endDate = dateToUse;
      
      const response = await fetchHostnames({
        ips: ipsNeedingHostnames,
        startDate,
        endDate,
      });

      // Extract data from AxiosResponse if needed
      const responseData = (response as any)?.data || response;

      // Map IPs to hostnames from response
      let hostnameMap: Record<string, string> = {};
      if (responseData && Array.isArray(responseData)) {
        responseData.forEach((item: any) => {
          if (item.ip && item.hostname) {
            hostnameMap[item.ip] = item.hostname;
          }
        });
      } else if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
        // Handle case where response is an object with IPs as keys
        hostnameMap = responseData as unknown as Record<string, string>;
      }
      
      // Cache the hostnames
      hostnameCacheRef.current[cacheKey] = hostnameMap;
      setIpHostnames((prev) => ({ ...prev, ...existingHostnames, ...hostnameMap }));
      if (isMailbox) {
        setLoadedMailboxIpBatches((prev) => new Set(prev).add(batchIndex));
      } else {
        setLoadedSenderProfileIpBatches((prev) => new Set(prev).add(batchIndex));
      }
    } catch (error) {
      console.error("Failed to fetch hostnames:", error);
      toast.error("Failed to fetch hostnames for IPs");
    }
  }, [loadedMailboxIpBatches, loadedSenderProfileIpBatches, selectedDate, selectedMailboxIpHostnames, selectedSenderProfileIpHostnames, fetchHostnames, extractHostnamesFromResponse]);

  // Track previous selected date/IPs to detect changes
  const prevSelectionRef = useRef<{ date: string; mailboxIps: string[]; senderProfileIps: string[] }>({ 
    date: "", 
    mailboxIps: [], 
    senderProfileIps: [] 
  });

  // Initialize hostnames from API response when dialog opens
  useEffect(() => {
    if (showSenderIpsDialog) {
      // Extract hostnames from API response if available
      const mailboxHostnames = extractHostnamesFromResponse(selectedMailboxIpHostnames);
      const senderProfileHostnames = extractHostnamesFromResponse(selectedSenderProfileIpHostnames);
      
      // Merge all hostnames (from API response)
      const hostnamesFromResponse = { ...mailboxHostnames, ...senderProfileHostnames };
      setIpHostnames((prev) => ({ ...prev, ...hostnamesFromResponse }));
      
      // Check if selection changed
      const selectionChanged = 
        prevSelectionRef.current.date !== selectedDate ||
        prevSelectionRef.current.mailboxIps.join(',') !== selectedMailboxIps.slice(0, BATCH_SIZE).sort().join(',') ||
        prevSelectionRef.current.senderProfileIps.join(',') !== selectedSenderProfileIps.slice(0, BATCH_SIZE).sort().join(',');
      
      if (selectionChanged) {
        // Reset state for new selection
        setVisibleMailboxIpCount(50);
        setVisibleSenderProfileIpCount(50);
        setLoadedMailboxIpBatches(new Set());
        setLoadedSenderProfileIpBatches(new Set());
        prevSelectionRef.current = { 
          date: selectedDate, 
          mailboxIps: selectedMailboxIps.slice(0, BATCH_SIZE),
          senderProfileIps: selectedSenderProfileIps.slice(0, BATCH_SIZE)
        };
      }
    }
  }, [showSenderIpsDialog, selectedDate, selectedMailboxIps, selectedSenderProfileIps, selectedMailboxIpHostnames, selectedSenderProfileIpHostnames, extractHostnamesFromResponse]);

  // Fetch hostnames for initial batches when dialog opens
  useEffect(() => {
    if (showSenderIpsDialog) {
      // Fetch mailbox IPs if needed
      if (visibleMailboxIps.length > 0 && !loadedMailboxIpBatches.has(0)) {
        const initialBatch = visibleMailboxIps.slice(0, BATCH_SIZE);
        const ipsNeedingHostnames = initialBatch.filter(ip => !ipHostnames[ip]);
        
        if (ipsNeedingHostnames.length > 0) {
          setIsLoadingHostnames(true);
          fetchHostnamesForBatch(ipsNeedingHostnames, 0, true).finally(() => {
            setIsLoadingHostnames(false);
          });
        } else {
          // All hostnames already available
          setLoadedMailboxIpBatches((prev) => new Set(prev).add(0));
        }
      }
      
      // Fetch sender profile IPs if needed
      if (visibleSenderProfileIps.length > 0 && !loadedSenderProfileIpBatches.has(0)) {
        const initialBatch = visibleSenderProfileIps.slice(0, BATCH_SIZE);
        const ipsNeedingHostnames = initialBatch.filter(ip => !ipHostnames[ip]);
        
        if (ipsNeedingHostnames.length > 0) {
          setIsLoadingHostnames(true);
          fetchHostnamesForBatch(ipsNeedingHostnames, 0, false).finally(() => {
            setIsLoadingHostnames(false);
          });
        } else {
          // All hostnames already available
          setLoadedSenderProfileIpBatches((prev) => new Set(prev).add(0));
        }
      }
    }
  }, [showSenderIpsDialog, visibleMailboxIps.length, visibleSenderProfileIps.length, loadedMailboxIpBatches, loadedSenderProfileIpBatches, ipHostnames, fetchHostnamesForBatch]);

  // Handle scroll to load more mailbox IPs
  useEffect(() => {
    const scrollContainer = mailboxScrollContainerRef.current;
    if (!scrollContainer || !showSenderIpsDialog) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Load more when user scrolls to 80% of the content
      if (scrollPercentage > 0.8 && visibleMailboxIpCount < selectedMailboxIps.length && !loadingMoreMailboxIps) {
        setLoadingMoreMailboxIps(true);
        
        // Calculate next batch
        const nextBatchStart = visibleMailboxIpCount;
        const nextBatchEnd = Math.min(nextBatchStart + BATCH_SIZE, selectedMailboxIps.length);
        const nextBatch = selectedMailboxIps.slice(nextBatchStart, nextBatchEnd);
        const batchIndex = Math.floor(nextBatchStart / BATCH_SIZE);
        const ipsNeedingHostnames = nextBatch.filter(ip => !ipHostnames[ip]);

        // Load hostnames for the new batch (only for IPs that need them)
        if (ipsNeedingHostnames.length > 0) {
          fetchHostnamesForBatch(ipsNeedingHostnames, batchIndex, true).then(() => {
            setVisibleMailboxIpCount(nextBatchEnd);
          }).finally(() => {
            setLoadingMoreMailboxIps(false);
          });
        } else {
          // All hostnames already available
          setVisibleMailboxIpCount(nextBatchEnd);
          setLoadingMoreMailboxIps(false);
        }
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [showSenderIpsDialog, visibleMailboxIpCount, selectedMailboxIps.length, loadingMoreMailboxIps, ipHostnames, fetchHostnamesForBatch]);

  // Handle scroll to load more sender profile IPs
  useEffect(() => {
    const scrollContainer = senderProfileScrollContainerRef.current;
    if (!scrollContainer || !showSenderIpsDialog) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Load more when user scrolls to 80% of the content
      if (scrollPercentage > 0.8 && visibleSenderProfileIpCount < selectedSenderProfileIps.length && !loadingMoreSenderProfileIps) {
        setLoadingMoreSenderProfileIps(true);
        
        // Calculate next batch
        const nextBatchStart = visibleSenderProfileIpCount;
        const nextBatchEnd = Math.min(nextBatchStart + BATCH_SIZE, selectedSenderProfileIps.length);
        const nextBatch = selectedSenderProfileIps.slice(nextBatchStart, nextBatchEnd);
        const batchIndex = Math.floor(nextBatchStart / BATCH_SIZE);
        const ipsNeedingHostnames = nextBatch.filter(ip => !ipHostnames[ip]);

        // Load hostnames for the new batch (only for IPs that need them)
        if (ipsNeedingHostnames.length > 0) {
          fetchHostnamesForBatch(ipsNeedingHostnames, batchIndex, false).then(() => {
            setVisibleSenderProfileIpCount(nextBatchEnd);
          }).finally(() => {
            setLoadingMoreSenderProfileIps(false);
          });
        } else {
          // All hostnames already available
          setVisibleSenderProfileIpCount(nextBatchEnd);
          setLoadingMoreSenderProfileIps(false);
        }
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [showSenderIpsDialog, visibleSenderProfileIpCount, selectedSenderProfileIps.length, loadingMoreSenderProfileIps, ipHostnames, fetchHostnamesForBatch]);

  // Create columns with cell renderers; override Warmup Email Sent & Engagement Rate to use dailyEmailsSent when send is 0
  const columnsWithRenderers = useMemo(() => {
    const baseColumns = columns.map((col) => {
      if (col.headerName === "Warmup Email Sent") {
        return {
          ...col,
          valueGetter: (params: any) => {
            const send = params.data?.send ?? 0;
            const opens = params.data?.opens ?? 0;
            const fallbackCount = emailsSentByDate[params.data?.date] ?? 0;
            const effectiveSend = send === 0 ? fallbackCount : send;
            return effectiveSend === 0 ? effectiveSend : Math.max(effectiveSend, opens);
          },
        };
      }
      if (col.headerName === "Engagement Rate") {
        return {
          ...col,
          valueGetter: (params: any) => {
            const totalInteractions = params.data?.opens ?? 0;
            let totalEmails = params.data?.send ?? 0;
            if (totalEmails === 0) {
              totalEmails = emailsSentByDate[params.data?.date] ?? 0;
            }
            if (params.data?.mailboxtemperature) {
              totalEmails = Math.min(totalEmails, params.data.mailboxtemperature);
            }
            if (totalEmails === 0) totalEmails = params.data?.opens ?? 0;
            const rawPercent = (totalInteractions / totalEmails) * 100;
            const capped = Math.min(rawPercent, 100);
            return `${capped.toFixed(1)}%`;
          },
        };
      }
      return col;
    });
    return [
      ...baseColumns,
      {
        headerName: "Mailbox IPs",
        headerTooltip: "Mailbox IPs",
        field: "mailboxIps",
        minWidth: 150,
        cellRenderer: MailboxIpsCellRenderer,
      },
      {
        headerName: "Sender Profile IPs",
        headerTooltip: "Sender Profile IPs",
        field: "senderProfileIps",
        minWidth: 150,
        cellRenderer: SenderProfileIpsCellRenderer,
      },
      {
        headerName: "SPF",
        headerTooltip: "SPF",
        field: "spf",
        minWidth: 150,
        cellRenderer: SpfCellRenderer,
      },
      {
        headerName: "DKIM",
        headerTooltip: "DKIM",
        field: "dkim",
        minWidth: 150,
        cellRenderer: DkimCellRenderer,
      },
      {
        headerName: "DMARC",
        headerTooltip: "DMARC",
        field: "dmarc",
        minWidth: 150,
        cellRenderer: DmarcCellRenderer,
      },
      {
        headerName: "Email Template Received (Sender Profiles)",
        headerTooltip: "Email Template Received (Sender Profiles)",
        field: "firstEmailBody",
        type: "textColumn",
        minWidth: 200,
        cellRenderer: EmailTemplateCellRenderer,
      },
    ];
  }, [emailsSentByDate]);

  const exportToCSV = () => {
    if (!gridRef.current?.api) return;

    const params = {
      fileName: "email-statistics.csv",
    };

    gridRef.current.api.exportDataAsCsv(params);
  };

  return (
    <Card className="rounded-lg shadow-none col-span-full">
      <CardHeader className="p-4 flex flex-col md:flex-row items-start md:items-end md:justify-between space-y-0 gap-2">
        <div className="space-y-1 w-fit">
          <CardTitle className="">Daily Email Statistics</CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            Daily breakdown of email engagement metrics
          </CardDescription>
        </div>
        <div className="flex items-center gap-2 w-full md:w-fit justify-end">
          <Button variant="outline" onClick={exportToCSV}>
            <Download size={16} />
            Export
          </Button>
          <ColumnVisibility
            gridRef={gridRef}
            storageKey="reputation-grid-columns"
          />
        </div>
      </CardHeader>

      <div className="p-2 pt-0">
        <DataTable
          ref={gridRef}
          rowData={data}
          columnDefs={columnsWithRenderers}
          height={700}
          defaultColDef={{
            minWidth: 130,
          }}
          loading={isLoading}
          // showPagination={true}
          // paginationProps={{
          //   pageSize: 100,
          //   totalPages: 1,
          //   currentPage: 1,
          //   onPageChange: (page) => {
          //     console.log(page);
          //   },
          // }}
        />
      </div>

      {/* Sender IPs Dialog */}
      <Dialog 
        open={showSenderIpsDialog} 
        onOpenChange={(open) => {
          setShowSenderIpsDialog(open);
          if (!open) {
            // Reset UI state when dialog closes, but keep cache
            setIsLoadingHostnames(false);
            setVisibleMailboxIpCount(50);
            setVisibleSenderProfileIpCount(50);
            setLoadingMoreMailboxIps(false);
            setLoadingMoreSenderProfileIps(false);
            // Don't clear ipHostnames and loaded batches - they'll be restored from cache on reopen
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl font-semibold">
                  {selectedMailboxIps.length > 0 ? "Mailbox IPs" : "Sender Profile IPs"}
                </DialogTitle>
                {(selectedMailboxIps.length > 0 || selectedSenderProfileIps.length > 0) && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedMailboxIps.length + selectedSenderProfileIps.length} total IP{selectedMailboxIps.length + selectedSenderProfileIps.length !== 1 ? 's' : ''} • {
                      Object.values({...selectedMailboxIpCounts, ...selectedSenderProfileIpCounts}).reduce((sum, count) => sum + (count || 0), 0)
                    } emails
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isExportingIps}
                  onClick={async () => {
                    const date = selectedDate || formatDateLocal(new Date());
                    setIsExportingIps(true);
                    try {
                      const sources: Array<"mailbox" | "sender"> = [];
                      if (selectedMailboxIps.length > 0) sources.push("mailbox");
                      if (selectedSenderProfileIps.length > 0) sources.push("sender");
                      if (sources.length === 0) {
                        sources.push("mailbox", "sender");
                      }
                      for (const ipSource of sources) {
                        const blob = await statisticsService.downloadIps({ date, ipSource }) as unknown as Blob;
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `ips-${ipSource}-${date}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }
                      toast.success("IPs exported successfully");
                    } catch {
                      toast.error("Failed to export IPs");
                    } finally {
                      setIsExportingIps(false);
                    }
                  }}
                >
                  {isExportingIps ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Export
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="mt-4 flex-1 overflow-hidden flex flex-col">
            {/* Mailbox IPs Table */}
            {selectedMailboxIps.length > 0 && (
              <div className="flex-1 flex flex-col min-h-0 mb-6">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Mailbox IPs
                </h3>
                <div 
                  ref={mailboxScrollContainerRef}
                  className="flex-1 overflow-y-auto border rounded-lg [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50"
                >
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead className="w-[35%]">IP ADDRESS</TableHead>
                        <TableHead className="w-[45%]">HOSTNAME</TableHead>
                        <TableHead className="w-[20%] text-right">NO. OF EMAILS</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleMailboxIps.map((ip, index) => {
                        const hostnameFromResponse = selectedMailboxIpHostnames[ip]?.hostname;
                        const hostname = hostnameFromResponse || ipHostnames[ip];
                        const batchIndex = Math.floor(index / BATCH_SIZE);
                        const isHostnameLoading = !hostnameFromResponse && !loadedMailboxIpBatches.has(batchIndex) && isLoadingHostnames && index < BATCH_SIZE;
                        const emailCount = getIpCount(selectedMailboxIpCounts, ip);
                        
                        return (
                          <TableRow key={`mailbox-${index}`} className="hover:bg-muted/50">
                            <TableCell className="font-mono text-sm">
                              <div className="flex items-center gap-2">
                                <span className="break-all">{ip}</span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(ip);
                                    toast.success("IP copied to clipboard");
                                  }}
                                  className="p-1 hover:bg-accent rounded transition-colors"
                                  title="Copy IP"
                                >
                                  <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                                </button>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {isHostnameLoading ? (
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground italic">Loading...</span>
                                </div>
                              ) : hostname ? (
                                <div className="flex items-center gap-2">
                                  <span className="break-all">{hostname}</span>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(hostname);
                                      toast.success("Hostname copied to clipboard");
                                    }}
                                    className="p-1 hover:bg-accent rounded transition-colors"
                                    title="Copy hostname"
                                  >
                                    <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground italic">No hostname available</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant="secondary" className="font-medium">
                                {emailCount}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  
                  {loadingMoreMailboxIps && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Loading more...</span>
                    </div>
                  )}
                  
                  {visibleMailboxIpCount >= selectedMailboxIps.length && selectedMailboxIps.length > 0 && (
                    <div className="text-center py-4 text-sm text-muted-foreground border-t">
                      Showing all {selectedMailboxIps.length} mailbox IP{selectedMailboxIps.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sender Profile IPs Table */}
            {selectedSenderProfileIps.length > 0 && (
              <div className="flex-1 flex flex-col min-h-0">
                {/* <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Users2 className="h-4 w-4" />
                  Sender Profile IPs
                </h3> */}
                <div 
                  ref={senderProfileScrollContainerRef}
                  className="flex-1 overflow-y-auto border rounded-lg [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50"
                >
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead className="w-[35%]">IP ADDRESS</TableHead>
                        <TableHead className="w-[45%]">HOSTNAME</TableHead>
                        <TableHead className="w-[20%] text-right">NO. OF EMAILS</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleSenderProfileIps.map((ip, index) => {
                        const hostnameFromResponse = selectedSenderProfileIpHostnames[ip]?.hostname;
                        const hostname = hostnameFromResponse || ipHostnames[ip];
                        const batchIndex = Math.floor(index / BATCH_SIZE);
                        const isHostnameLoading = !hostnameFromResponse && !loadedSenderProfileIpBatches.has(batchIndex) && isLoadingHostnames && index < BATCH_SIZE;
                        const emailCount = getIpCount(selectedSenderProfileIpCounts, ip);
                        
                        return (
                          <TableRow key={`sender-profile-${index}`} className="hover:bg-muted/50">
                            <TableCell className="font-mono text-sm">
                              <div className="flex items-center gap-2">
                                <span className="break-all">{ip}</span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(ip);
                                    toast.success("IP copied to clipboard");
                                  }}
                                  className="p-1 hover:bg-accent rounded transition-colors"
                                  title="Copy IP"
                                >
                                  <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                                </button>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {isHostnameLoading ? (
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground italic">Loading...</span>
                                </div>
                              ) : hostname ? (
                                <div className="flex items-center gap-2">
                                  <span className="break-all">{hostname}</span>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(hostname);
                                      toast.success("Hostname copied to clipboard");
                                    }}
                                    className="p-1 hover:bg-accent rounded transition-colors"
                                    title="Copy hostname"
                                  >
                                    <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground italic">No hostname available</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant="secondary" className="font-medium">
                                {emailCount}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  
                  {loadingMoreSenderProfileIps && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Loading more...</span>
                    </div>
                  )}
                  
                  {visibleSenderProfileIpCount >= selectedSenderProfileIps.length && selectedSenderProfileIps.length > 0 && (
                    <div className="text-center py-4 text-sm text-muted-foreground border-t">
                      Showing all {selectedSenderProfileIps.length} sender profile IP{selectedSenderProfileIps.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* No IPs message */}
            {selectedMailboxIps.length === 0 && selectedSenderProfileIps.length === 0 && (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-muted-foreground text-center py-8">
                  No sender IPs available
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Template Dialog */}
      <Dialog open={showEmailTemplateDialog} onOpenChange={setShowEmailTemplateDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Email Template{selectedEmailTemplates.length > 1 ? `s (${selectedEmailTemplates.length})` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex-1 overflow-hidden">
            {selectedEmailTemplates.length > 0 ? (
              <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-4 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
                {selectedEmailTemplates.map((template, index) => (
                  <div
                    key={index}
                    className="space-y-3"
                  >
                    {/* Template Header */}
                    <div className="p-4 bg-muted rounded-lg border space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-semibold">{template.name}</span>
                          <span className="text-xs text-muted-foreground">{template.sender_email}</span>
                        </div>
                      </div>
                      {template.subject && (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-muted-foreground uppercase">Subject</span>
                          <span className="text-sm font-medium">{template.subject}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Template Body Preview */}
                    <div className="border rounded-lg overflow-hidden">
                      <iframe
                        srcDoc={template.body}
                        sandbox="allow-same-origin"
                        className="w-full h-[400px] border-0"
                        title={`Email Template Preview - ${template.sender_email}`}
                      />
                    </div>
                    
                    {index < selectedEmailTemplates.length - 1 && (
                      <div className="border-t pt-4" />
                    )}
                  </div>
                ))}
              </div>
            ) : selectedEmailTemplate ? (
              <div className="max-h-[60vh] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
                <div 
                  className="p-4 bg-background rounded-lg border prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedEmailTemplate }}
                />
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No email template available
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

function DomainStatusChart({
  dashboardStats,
}: {
  dashboardStats: {
    totalValue: number;
    highReputation: { value: number; percentage: number };
    goodSpamRate: { value: number; percentage: number };
    needsAttention: { value: number; percentage: number };
  };
}) {
  // Transform data for pie chart
  const chartData = [
    {
      name: "High Reputation",
      value: dashboardStats.highReputation.value,
      percentage: dashboardStats.highReputation.percentage,
      color: "#22c55e", // green
    },
    {
      name: "Needs Attention",
      value: dashboardStats.needsAttention.value,
      percentage: dashboardStats.needsAttention.percentage,
      color: "#ef4444", // red
    },
  ];

  return (
    <Card className="col-span-6 shadow-sm h-full">
      <CardHeader className="p-4">
        <CardTitle>Google Postmaster Insights</CardTitle>
        <CardDescription>
          Summary of your domain's performance metrics from Google Postmaster
          Tools
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div>
          {/* Doughnut Chart with Center Text */}
          <div className="relative h-[300px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload?.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="rounded-lg border bg-background p-3 shadow-lg">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: data.color }}
                            />
                            <span className="text-sm font-medium">
                              {data.name}
                            </span>
                          </div>
                          <div className="mt-2 space-y-1.5">
                            <div className="flex items-center justify-between gap-4">
                              <span className="text-xs text-muted-foreground">
                                Domains
                              </span>
                              <span className="text-sm font-medium">
                                {data.value}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                              <span className="text-xs text-muted-foreground">
                                Percentage
                              </span>
                              <span className="text-sm font-medium">
                                {data.percentage}%
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center Text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-sm text-muted-foreground">Total Value</div>
              <div className="text-3xl font-bold">
                {dashboardStats.totalValue}
              </div>
            </div>
          </div>

          {/* Table View */}
          <div className="w-full flex flex-col items-center justify-center">
            <div className="w-full border rounded-md px-4 py-2">
              <div className="grid grid-cols-3 text-xs text-muted-foreground pb-2">
                <div>Label</div>
                <div>Value</div>
                <div>%</div>
              </div>
              {chartData.map((item, index) => (
                <div key={index} className="grid grid-cols-3 items-center py-1">
                  <div className="flex items-center gap-2">
                    <div
                      className="size-2 rounded-[2px]"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm">{item.name}</span>
                  </div>
                  <div className="text-sm font-medium">{item.value}</div>
                  <div className="text-sm">{item.percentage}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const EmailSentChart = ({
  data,
  dailyStats,
  dailyEmailsSent,
  className,
}: {
  data?: Array<{ sentDate: string; count: number }>;
  dailyStats?: DailyStatistic[];
  dailyEmailsSent?: Array<{ date: string; count: number }>;
  className?: string;
}) => {
  // Prefer dailyStats: use max(send, opens) per day; when send is 0, use dailyEmailsSent count for that date
  const finalChartData = useMemo(() => {
    if (dailyStats && dailyStats.length > 0) {
      const emailsSentByDate = (dailyEmailsSent || []).reduce(
        (acc, item) => {
          acc[item.date] = item.count ?? 0;
          return acc;
        },
        {} as Record<string, number>
      );
      return dailyStats
        .map((d) => {
          const send = d.send ?? 0;
          const opens = d.opens ?? 0;
          const fallbackCount = emailsSentByDate[d.date] ?? 0;
          const effectiveSend = send === 0 ? fallbackCount : send;
          return {
            date: d.date,
            emailsSent: effectiveSend === 0 ? effectiveSend : Math.max(effectiveSend, opens),
          };
        })
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
          if (isNaN(dateA.getTime())) return 1;
          if (isNaN(dateB.getTime())) return -1;
          return dateA.getTime() - dateB.getTime();
        });
    }

    // Fallback: transform mailboxSeedDailyStats, grouping by date and taking max count per date
    const byDate = (data || []).reduce(
      (acc, curr) => {
        let dateObj = new Date(curr.sentDate);
        if (isNaN(dateObj.getTime())) {
          dateObj = new Date(curr.sentDate + "T00:00:00.000Z");
        }
        if (isNaN(dateObj.getTime())) return acc;
        const date = formatDateLocal(dateObj);
        if (!acc[date]) acc[date] = { date, emailsSent: 0 };
        acc[date].emailsSent = Math.max(acc[date].emailsSent, curr.count ?? 0);
        return acc;
      },
      {} as Record<string, { date: string; emailsSent: number }>
    );

    const chartData = Object.values(byDate).sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      return dateA.getTime() - dateB.getTime();
    });

    if (chartData.length === 0 && data?.length) {
      return data.map((item) => ({
        date: item.sentDate,
        emailsSent: item.count ?? 0,
      }));
    }
    return chartData;
  }, [data, dailyStats, dailyEmailsSent]);

  return (
    <Card className="col-span-12 w-full">
      <CardHeader className="p-4">
        <CardTitle>Daily Emails Sent</CardTitle>
        <CardDescription>Number of warmup emails sent per day</CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <ChartContainer className="h-[300px] w-full" config={chartConfig}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={finalChartData}>
              <defs>
                <linearGradient
                  id="emailsSentGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="hsl(var(--primary))"
                    stopOpacity={0.2}
                  />
                  <stop
                    offset="95%"
                    stopColor="hsl(var(--primary))"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={(date) => {
                  const dateObj = new Date(date);
                  return isNaN(dateObj.getTime()) ? "Invalid Date" : format(dateObj, "MMM dd");
                }}
                tickLine={false}
                axisLine={false}
                dy={10}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                dx={-10}
                tickFormatter={(value, index) =>
                  index === 0 ? "0" : Math.round(value).toString()
                }
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload?.length) {
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-lg">
                        <div className="grid gap-2">
                          <div className="flex flex-col">
                            <span className="text-[0.70rem] uppercase text-muted-foreground">
                              Date
                            </span>
                            <span className="font-semibold">
                              {(() => {
                                const dateObj = new Date(payload[0].payload.date);
                                return isNaN(dateObj.getTime()) 
                                  ? "Invalid Date" 
                                  : format(dateObj, "MMM dd, yyyy");
                              })()}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[0.70rem] uppercase text-muted-foreground">
                              Emails Sent
                            </span>
                            <span className="font-semibold text-primary">
                              {payload[0].value}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="emailsSent"
                stroke="hsl(var(--primary))"
                fill="url(#emailsSentGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

interface DraggableCardProps {
  children: React.ReactNode;
  index: number;
  moveCard: (fromIndex: number, toIndex: number) => void;
  className?: string;
}

interface DragItem {
  index: number;
  type: string;
}

const DraggableCard = ({
  children,
  index,
  moveCard,
  className,
}: DraggableCardProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: "DASHBOARD_CARD",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>(
    () => ({
      accept: "DASHBOARD_CARD",
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      hover: (item: DragItem, monitor) => {
        if (!ref.current) return;
        const dragIndex = item.index;
        const hoverIndex = index;

        if (dragIndex === hoverIndex) return;

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        moveCard(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    })
  );

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-200 ease-out ${
        isOver ? "scale-105" : ""
      } ${isDragging ? "opacity-50 scale-95" : "opacity-100"}`}
    >
      {children}
    </div>
  );
};
